import { useTheme } from '@mui/material/styles';
import { FunctionComponent } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export interface ChartProp {
    name: string;
    male: number;
    female: number;
    unknown: number;
}

interface BarChartProps {
    data: ChartProp[];
}

const COLORS: string[] = ['#4B8CBE', '#00579A', '#124288'];



type CircleLegendProps = {
    label: string,
    color: string,
}

const CircleLegend: FunctionComponent<CircleLegendProps> = ({ label, color }) => {

    return (
        <Stack direction="column" sx={{ alignItems: 'left' }} spacing={1}>
            <Stack
                direction="row" sx={{
                    padding: '2px 10px 2px 10px',
                }}>
                <Box sx={{
                    width: 22,
                    borderRadius: '50%',
                    height: 19,
                    backgroundColor: color,
                    padding: '0 0 0 0',
                }}>&nbsp;
                </Box>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                }}>
                    <Typography sx={{
                        pr: {
                            xs: 0,
                            sm: 1,
                            md: 2
                        },
                        pl: {
                            xs: '2px',
                            sm: 1,
                            md: 2
                        },
                        pb: {
                            xs: 0,
                            sm: 1,
                            md: 2
                        },
                        fontSize: {
                            xs: '0.8rem',
                            sm: '0.9rem',
                            md: '1rem'
                        },
                        color: 'primary.main',
                    }}>
                        {label}
                    </Typography>

                </Box>
            </Stack>
        </Stack>
    )
}

function camelize(str: string) {
    const camelcaseString = str[0].toUpperCase() + str.substring(1);
    return camelcaseString;
}

const renderLegend = (props: any) => {
    const { payload } = props;

    return (
        <Stack direction="row" 
            sx={{
                flexWrap: 'wrap',
                mt: '2px',
                pl: {
                    xs: 0,
                    md: 10
                },
                ml: {
                    xs: 0,
                    md: '22%',
                    lg: '35%',
                    xl: '43%'
                }
            }}
            spacing={3}>
            {
                payload.map((entry: any, i: number) => {
                    return (
                        <CircleLegend key={i} label={camelize(entry.dataKey)} color={COLORS[i % COLORS.length]} />
                    )
                })
            }
        </Stack>
    )
}

export default function CountByAgeChart({ data }: BarChartProps) {
    const theme = useTheme();

    const formatter = (label: any, value: any) => { return [label, value] };
    return (
        <ResponsiveContainer >
            <BarChart
                width={500}
                height={300}
                data={data}

                margin={{
                    top: 10,
                    right: 40,
                    left: 20,
                    bottom: 20,
                }}
                barSize={7}
            >
                <CartesianGrid stroke={theme.palette.text.secondary} vertical={false} />
                <XAxis
                    dataKey="name"
                    stroke={theme.palette.text.secondary}
                    dy={10}
                    tickLine={false}
                    axisLine={false}
                    strokeDasharray="3 3"
                    style={{
                        fontSize: '0.8rem',
                        fontFamily: 'Montserrat',
                        fontWeight: 100
                    }}
                />

                <YAxis
                    dx={-15}
                    stroke='gray'
                    tickLine={false}
                    axisLine={false}
                />
                <Tooltip cursor={false} formatter={formatter} />
                <Legend content={renderLegend} verticalAlign="bottom" wrapperStyle={{ bottom: 0 }}/>
                {data.length > 0 &&
                    Object.keys(data[0]).map((k, i) =>
                        k !== 'name' && <Bar key={i} dataKey={k} fill={COLORS[i - 1]} radius={[8, 8, 0, 0]} barSize={10} />
                    )
                }


            </BarChart>
        </ResponsiveContainer>
    )
}

